import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useCaregiver } from '../../hooks/useCaregiver';
import { API_BASE_URL } from '../../config/apiUrl';

interface TrialIncompleteBannerProps {
  seniorId: string;
  seniorName: string;
}

/**
 * Shown when a caregiver has completed signup + saved a senior config but
 * never finished the Stripe checkout. Offers a one-click path to resume
 * Stripe checkout with the existing senior.
 *
 * Detection (done at the Dashboard level): senior exists AND
 * stripe_customer_id is null (never completed checkout).
 */
export const TrialIncompleteBanner: FC<TrialIncompleteBannerProps> = ({
  seniorId,
  seniorName,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { caregiver } = useCaregiver();
  const [loading, setLoading] = useState(false);

  const handleResume = async () => {
    setLoading(true);
    try {
      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          seniorId,
          planTier: 'plus',
          caregiverEmail: caregiver?.email || user?.email || '',
          caregiverFirstName: caregiver?.full_name?.split(' ')[0] || '',
          seniorName,
          language: i18n.language?.split('-')[0] || 'fr',
        }),
      });
      if (!response.ok) throw new Error('Failed to create checkout session');
      const { url } = await response.json();
      if (url) window.location.href = url;
    } catch (err) {
      console.error('[TrialIncompleteBanner] Failed to resume checkout:', err);
      setLoading(false);
    }
  };

  return (
    <div
      role="alert"
      className="flex items-start gap-4 p-4 rounded-2xl border border-amber-200 mb-4"
      style={{
        background:
          'linear-gradient(135deg, rgba(254, 243, 199, 0.9) 0%, rgba(254, 249, 231, 0.9) 100%)',
      }}
    >
      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
        <span className="material-symbols-outlined text-amber-700" style={{ fontSize: '22px' }}>
          warning
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-display font-bold text-amber-900">
          {t('trialBanner.title')}
        </h3>
        <p className="text-xs text-amber-900/80 mt-0.5 leading-relaxed">
          {t('trialBanner.body')}
        </p>
      </div>
      <button
        onClick={handleResume}
        disabled={loading}
        className="flex-shrink-0 px-4 py-2 bg-brand-accent text-white text-sm font-bold rounded-xl hover:bg-[#c96a3f] transition-colors disabled:opacity-50 shadow-[0_2px_8px_rgba(224,123,76,0.25)]"
      >
        {loading ? t('trialBanner.loading') : t('trialBanner.cta')}
      </button>
    </div>
  );
};

export default TrialIncompleteBanner;
